// Apple App Store top-level software category genre IDs.
// Source: https://affiliate.itunes.apple.com/resources/documentation/genre-mapping/
// These IDs are stable and used in iTunes Search API genreId parameter.

/**
 * @typedef {{ id: string, name: string }} Genre
 */

/** @type {Genre[]} */
export const GENRES = [
  { id: '6000', name: 'Business' },
  { id: '6017', name: 'Education' },
  { id: '6016', name: 'Entertainment' },
  { id: '6015', name: 'Finance' },
  { id: '6023', name: 'Food & Drink' },
  { id: '6014', name: 'Games' },
  { id: '6013', name: 'Health & Fitness' },
  { id: '6012', name: 'Lifestyle' },
  { id: '6020', name: 'Medical' },
  { id: '6011', name: 'Music' },
  { id: '6010', name: 'Navigation' },
  { id: '6009', name: 'News' },
  { id: '6008', name: 'Photo & Video' },
  { id: '6007', name: 'Productivity' },
  { id: '6006', name: 'Reference' },
  { id: '6005', name: 'Social Networking' },
  { id: '6024', name: 'Sports' },
  { id: '6004', name: 'Travel' },
  { id: '6003', name: 'Utilities' },
  { id: '6002', name: 'Weather' },
];

// English is the canonical source of truth (the GENRES table above); the
// per-locale maps below only carry the translated strings. Keys are the same
// stable Apple genre ids, so a missing entry simply falls back to English.
/** @type {Record<string, string>} */
const EN_NAMES = Object.freeze(
  Object.fromEntries(GENRES.map((g) => [g.id, g.name])),
);

/**
 * Translated genre names keyed by locale, then by Apple genre id. Strings match
 * the category labels Apple uses on the localized App Store fronts. English is
 * intentionally omitted here — it lives in GENRES / EN_NAMES.
 * @type {Record<string, Record<string, string>>}
 */
export const GENRE_NAMES = Object.freeze({
  ja: Object.freeze({
    '6000': 'ビジネス',
    '6017': '教育',
    '6016': 'エンターテインメント',
    '6015': 'ファイナンス',
    '6023': 'フード/ドリンク',
    '6014': 'ゲーム',
    '6013': 'ヘルスケア/フィットネス',
    '6012': 'ライフスタイル',
    '6020': 'メディカル',
    '6011': 'ミュージック',
    '6010': 'ナビゲーション',
    '6009': 'ニュース',
    '6008': '写真/ビデオ',
    '6007': '仕事効率化',
    '6006': '辞書/辞典/その他',
    '6005': 'ソーシャルネットワーキング',
    '6024': 'スポーツ',
    '6004': '旅行',
    '6003': 'ユーティリティ',
    '6002': '天気',
  }),
  'zh-CN': Object.freeze({
    '6000': '商务',
    '6017': '教育',
    '6016': '娱乐',
    '6015': '财务',
    '6023': '美食佳饮',
    '6014': '游戏',
    '6013': '健康健美',
    '6012': '生活',
    '6020': '医疗',
    '6011': '音乐',
    '6010': '导航',
    '6009': '新闻',
    '6008': '摄影与录像',
    '6007': '效率',
    '6006': '参考',
    '6005': '社交',
    '6024': '体育',
    '6004': '旅游',
    '6003': '工具',
    '6002': '天气',
  }),
  'zh-Hant': Object.freeze({
    '6000': '商業',
    '6017': '教育',
    '6016': '娛樂',
    '6015': '財經',
    '6023': '美食佳飲',
    '6014': '遊戲',
    '6013': '健康塑身',
    '6012': '生活',
    '6020': '醫療',
    '6011': '音樂',
    '6010': '導航',
    '6009': '新聞',
    '6008': '攝影與錄影',
    '6007': '生產力工具',
    '6006': '參考資料',
    '6005': '社交',
    '6024': '運動',
    '6004': '旅遊',
    '6003': '工具程式',
    '6002': '天氣',
  }),
});

/**
 * Localized display name for an Apple genre id.
 *
 * Resolution order: the requested locale's translation, then the English name
 * (the canonical fallback for any locale without an entry), then the provided
 * `fallback`, then the raw id. The `fallback` is meant for ids outside our
 * top-level table — e.g. an Apple sub-genre where the feed already gave us its
 * English name — so a number never leaks into the UI.
 *
 * @param {string|number|null|undefined} genreId - Apple genre id (e.g. '6014').
 * @param {string} [locale='en'] - Target locale; unknown locales fall back to English.
 * @param {string} [fallback] - Used only when the id is unknown to every map.
 * @returns {string}
 */
export function genreName(genreId, locale = 'en', fallback) {
  const id = genreId == null ? '' : String(genreId);
  if (id) {
    const localeMap = GENRE_NAMES[locale];
    if (localeMap && localeMap[id]) return localeMap[id];
    if (EN_NAMES[id]) return EN_NAMES[id];
  }
  return fallback ?? id;
}
